'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import Image from 'next/image';

// Utils
import { routes } from '@routes';
import { useExperiments } from '@contexts/use-experiments';

// UI
import heroImage from '@public/assets/img/heros/homepage-hero-fall-2026.jpg';
import mobileHeroImage from '@public/assets/img/heros/homepage-hero-fall-2026-mobile.jpg';
import { MobileSearchBar } from '@components/mobile-search-bar';
import { SearchForm } from '@components/search-form';
import { MobileSearchModal } from '@components/mobile-search-modal';
import { MobileSearchModalProvider } from '@components/mobile-search-modal';
import { Typography } from '@components/typography';
import { HeroPriceComparison } from './hero-price-comparison';

// Headline copy for the homepage_header_copy experiment.
const HEADLINES: Record<string, string> = {
  control: 'Vacation rentals you want, at the prices you deserve.',
  price_focused: 'The vacation rental you want, for hundreds less.'
};
export const Hero: React.FC = () => {
  const $ = _c(28);
  const {
    homepageHeaderCopy
  } = useExperiments();
  const [hydrated, setHydrated] = React.useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setHydrated(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  const headlineFade = `transition-opacity duration-500 ease-out ${hydrated ? "opacity-100" : "opacity-0"}`;
  const comparisonFade = `transition-opacity duration-500 ease-out delay-200 ${hydrated ? "opacity-100" : "opacity-0"}`;
  const headline = HEADLINES[homepageHeaderCopy?.value ?? "control"] ?? HEADLINES.control;
  let t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Image fill={true} className="hidden overflow-hidden sm:block object-cover object-[45%_30%]" src={heroImage} alt="Savvy hero" placeholder="blur" blurDataURL={heroImage.blurDataURL} priority={true} fetchPriority="high" />;
    t3 = <div className="absolute inset-0 hidden sm:block opacity-70 bg-[linear-gradient(90deg,_rgba(0,0,0,0.80)_-0.5%,_rgba(0,0,0,0.38)_60.2%,_rgba(0,0,0,0.00)_86%)]" />;
    t4 = <div className="absolute inset-0 hidden sm:block opacity-70 bg-[linear-gradient(32.9deg,_rgba(0,0,0,0.40)_26.1%,_rgba(0,0,0,0.20)_48.9%,_rgba(0,0,0,0.00)_72.1%)]" />;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  const t5 = `max-w-[816px] text-white drop-shadow-lg !tracking-normal !text-5xl !leading-[1.1] lg:!text-h2 ${headlineFade}`;
  let t6;
  if ($[5] !== headline || $[6] !== t5) {
    t6 = <Typography component="p" variant="title3" aria-hidden="true" className={t5}>{headline}</Typography>;
    $[5] = headline;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const t7 = `mt-8 ${comparisonFade}`;
  let t8;
  if ($[8] !== t7) {
    t8 = <HeroPriceComparison className={t7} />;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mt-12 w-full lg:mt-[108px]"><SearchForm /></div>;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t6 || $[12] !== t8) {
    t10 = <div className="absolute inset-x-0 z-10 mx-auto hidden h-full w-full max-w-[1312px] flex-col justify-end px-8 pb-12 sm:flex lg:pb-16">{t6}{t8}{t9}</div>;
    $[11] = t6;
    $[12] = t8;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Image fill={true} className="block overflow-hidden object-cover object-center" src={mobileHeroImage} alt="Savvy hero" placeholder="blur" priority={true} fetchPriority="high" />;
    t12 = <div className="absolute inset-0 opacity-80 bg-[linear-gradient(224.4deg,_rgba(255,255,255,0.00)_5.8%,_rgba(130,130,130,0.34)_17.3%,_rgba(0,0,0,0.60)_54.5%)]" />;
    $[14] = t11;
    $[15] = t12;
  } else {
    t11 = $[14];
    t12 = $[15];
  }
  const t13 = `text-left text-off-white drop-shadow-lg !tracking-normal !text-[44px] !leading-[44px] ${headlineFade}`;
  let t14;
  if ($[16] !== headline || $[17] !== t13) {
    t14 = <Typography component="h1" variant="title4" className={t13}>{headline}</Typography>;
    $[16] = headline;
    $[17] = t13;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = `mt-10 ${comparisonFade}`;
  let t16;
  if ($[19] !== t15) {
    t16 = <HeroPriceComparison className={t15} />;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="mt-[89px] w-full"><MobileSearchModalProvider><MobileSearchModal onSubmit={_temp} /><MobileSearchBar /></MobileSearchModalProvider></div>;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  let t18;
  if ($[22] !== t14 || $[23] !== t16) {
    t18 = <div className="relative flex h-[662px] w-full items-center overflow-hidden sm:hidden">{t11}{t12}<div className="relative z-10 mx-auto flex h-full w-11/12 max-w-3xl flex-col items-start justify-end px-4 pb-10 sm:hidden">{t14}{t16}{t17}</div></div>;
    $[22] = t14;
    $[23] = t16;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  let t19;
  if ($[25] !== t10 || $[26] !== t18) {
    t19 = <div className="relative mx-auto flex w-full flex-col items-center justify-center sm:h-[600px] lg:h-[720px]">{t2}{t3}{t4}{t10}{t18}</div>;
    $[25] = t10;
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  return t19;
};
function _temp(vals) {
  window.location.assign(routes.searchShow({
    placeId: vals.placeId,
    checkInDate: vals.checkInDate || undefined,
    checkOutDate: vals.checkOutDate || undefined,
    adultCount: vals.adultCount,
    childCount: vals.childCount,
    infantCount: vals.infantCount,
    petCount: vals.petCount
  }));
}